/*
 * ViewerControls.jsx - Enhanced Download Functions with Meaningful Filenames
 *
 * This file contains improved download functionality that generates meaningful filenames
 * based on user prompts instead of random numbers.
 *
 * Key improvements:
 * - Extracts meaningful keywords from prompts (3-4 words max)
 * - Filters out common stop words
 * - Limits filename length to 35 characters (excluding extension)
 * - Adds file type prefixes when needed
 * - Preserves timestamp for uniqueness
 *
 * Example transformations:
 * "Create a 1-minute instrumental background music for a thriller song"
 * → "thriller-instrumental-background-music-1234.wav"
 *
 * "A beautiful sunset landscape with mountains"
 * → "beautiful-sunset-landscape-mountains-5678.png"
 *
 * "Generate a futuristic robot character in 3D"
 * → "3d-futuristic-robot-character-9012.glb"
 */

import React from 'react'
import { Button } from '@components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip'
import { RotateCcw, ZoomIn, ZoomOut, Maximize2, X, Grid3x3, Download } from 'lucide-react'

// Utility function to extract meaningful keywords from text
const extractKeywords = (text, maxWords = 3) => {
  if (!text || typeof text !== 'string') return []

  // Common words to filter out (expanded list)
  const stopWords = new Set([
    'a',
    'an',
    'and',
    'are',
    'as',
    'at',
    'be',
    'by',
    'for',
    'from',
    'has',
    'he',
    'in',
    'is',
    'it',
    'its',
    'of',
    'on',
    'that',
    'the',
    'to',
    'was',
    'will',
    'with',
    'create',
    'generate',
    'make',
    'an',
    'image',
    'picture',
    'photo',
    'video',
    'music',
    'audio',
    'model',
    '3d',
    'ai',
    'using',
    'please',
    'can',
    'you',
    'me',
    'my',
    'i',
    'we',
    'they',
    'this',
    'that',
    'these',
    'those',
    'very',
    'much',
    'more',
    'most',
    'some',
    'all',
    'any',
    'each',
    'every',
    'both',
    'either',
    'neither',
    'one',
    'two',
    'three',
    'first',
    'second',
    'third',
    'high',
    'low',
    'big',
    'small',
    'new',
    'old',
    'good',
    'bad',
    'long',
    'short',
    'hot',
    'cold',
    'light',
    'dark',
    'heavy',
    'soft',
  ])

  // Extract and clean words
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .split(/\s+/) // Split on whitespace
    .filter(
      (word) =>
        word.length > 2 && // Min 3 characters
        word.length < 12 && // Max 12 characters (shorter for better readability)
        !stopWords.has(word) && // Not a stop word
        !/^\d+$/.test(word) && // Not just numbers
        /^[a-z]/.test(word) // Starts with letter
    )

  // Prioritize nouns and descriptive words (simple heuristic)
  const prioritizedWords = words.sort((a, b) => {
    // Prioritize longer, more descriptive words
    if (a.length !== b.length) return b.length - a.length
    return 0
  })

  // Take first few meaningful words
  return prioritizedWords.slice(0, maxWords)
}

// Utility function to generate meaningful file names
const generateMeaningfulFileName = (type, context = {}) => {
  const { mode = null, index = null, total = null, prompt = null, title = null } = context

  let baseName = ''

  // Try to use prompt or title keywords first
  const textToProcess = prompt || title || ''
  if (textToProcess) {
    const keywords = extractKeywords(textToProcess, 3)
    if (keywords.length > 0) {
      baseName = keywords.join('-')

      // Add type prefix only if keywords don't clearly indicate the type
      const typeKeywords = [
        'portrait',
        'landscape',
        'photo',
        'painting',
        'drawing',
        'song',
        'beat',
        'melody',
        'track',
        'sculpture',
        'building',
        'model',
      ]
      const hasTypeKeyword = keywords.some((word) => typeKeywords.includes(word))

      if (!hasTypeKeyword && baseName.length < 15) {
        const typePrefix =
          type === 'image'
            ? 'img'
            : type === 'video'
              ? 'vid'
              : type === 'music' || type === 'audio'
                ? 'mus'
                : type === '3d' || type === 'model'
                  ? '3d'
                  : 'ai'
        baseName = `${typePrefix}-${baseName}`
      }
    }
  }

  // Fallback to generic names if no prompt or no meaningful keywords
  if (!baseName) {
    switch (type) {
      case 'image':
        baseName = mode === '3d' ? '3d-image' : mode === 'image' ? 'ai-image' : 'image'
        break

      case 'video':
        baseName = mode === 'video' ? 'ai-video' : 'video'
        break

      case 'audio':
      case 'music':
        baseName = mode === 'music' ? 'ai-music' : 'audio'
        break

      case '3d':
      case 'model':
        baseName = '3d-model'
        break

      default:
        baseName = 'ai-content'
    }
  }

  // Add index only if multiple files
  if (index !== null && total !== null && total > 1) {
    baseName += `-${index}`
  }

  // Add short timestamp for uniqueness (last 4 digits of timestamp)
  const shortTime = Date.now().toString().slice(-4)
  baseName += `-${shortTime}`

  // Ensure filename isn't too long (max 35 characters without extension)
  if (baseName.length > 35) {
    // Trim but preserve the timestamp
    const timeStamp = baseName.slice(-5) // -1234
    const mainPart = baseName.slice(0, -5)
    baseName = mainPart.substring(0, 30) + timeStamp
  }

  return baseName
}

// Alternative download method using canvas for images
const downloadImageViaCanvas = async (url, filename) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const url = window.URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = filename || 'ai-generated-image.png'
              link.style.display = 'none'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              window.URL.revokeObjectURL(url)
              resolve(true)
            } else {
              reject(new Error('Failed to create blob from canvas'))
            }
          },
          'image/png',
          0.9
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

// Robust download function that works on mobile and desktop
const downloadFileFromUrl = async (url, filename = null, options = {}) => {
  try {
    if (!url) {
      // No URL provided for download
      return
    }

    // Determine file type for better naming
    const isImage =
      url.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) || (filename && filename.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i))
    const isVideo =
      url.match(/\.(mp4|webm|mov|avi|mkv)$/i) || (filename && filename.match(/\.(mp4|webm|mov|avi|mkv)$/i))
    const isAudio =
      url.match(/\.(mp3|wav|ogg|m4a|flac|aac)$/i) || (filename && filename.match(/\.(mp3|wav|ogg|m4a|flac|aac)$/i))
    const is3D =
      url.match(/\.(glb|gltf|obj|fbx|ply|dae)$/i) || (filename && filename.match(/\.(glb|gltf|obj|fbx|ply|dae)$/i))

    // Generate meaningful filename if not provided or if it's generic
    let finalFilename = filename
    if (
      !finalFilename ||
      finalFilename.includes('download') ||
      finalFilename.includes('generated-') ||
      finalFilename.includes('image-') ||
      finalFilename.includes('video-') ||
      finalFilename.includes('track-')
    ) {
      let fileType = 'content'
      if (isImage) fileType = 'image'
      else if (isVideo) fileType = 'video'
      else if (isAudio) fileType = 'music'
      else if (is3D) fileType = '3d'

      finalFilename = generateMeaningfulFileName(fileType, options)
    }

    // For images, try canvas method first (works better on mobile)
    if (isImage) {
      // Detected image, trying canvas download method first
      try {
        // Ensure proper extension
        if (!finalFilename.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)) {
          finalFilename += '.png'
        }
        const result = await downloadImageViaCanvas(url, finalFilename)
        // Canvas download successful
        return result
      } catch (canvasError) {
        // Canvas download failed, falling back to fetch method
        // Continue with fetch method below
      }
    }

    // Fetch the file with additional headers for better compatibility
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: '*/*',
        'Cache-Control': 'no-cache',
      },
      mode: 'cors',
    })

    // Fetch response

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const blob = await response.blob()
    // Blob created

    if (blob.size === 0) {
      throw new Error('Downloaded file is empty')
    }

    // Ensure filename has proper extension
    if (!finalFilename.includes('.')) {
      // Try to determine extension from blob type or URL
      const mimeType = blob.type
      let extension = 'png' // Default for images
      if (mimeType.includes('video/mp4')) extension = 'mp4'
      else if (mimeType.includes('video')) extension = 'webm'
      else if (mimeType.includes('audio/mpeg')) extension = 'mp3'
      else if (mimeType.includes('audio')) extension = 'wav'
      else if (mimeType.includes('model') || is3D) extension = 'glb'
      else if (mimeType.includes('json')) extension = 'json'
      else if (url.includes('.jpg') || url.includes('.jpeg')) extension = 'jpg'
      else if (url.includes('.png')) extension = 'png'
      else if (url.includes('.gif')) extension = 'gif'
      else if (url.includes('.webp')) extension = 'webp'
      else if (url.includes('.mp4')) extension = 'mp4'
      else if (url.includes('.mp3')) extension = 'mp3'
      else if (url.includes('.glb')) extension = 'glb'

      finalFilename = `${finalFilename}.${extension}`
    }

    // Final filename

    // Create download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    // Set attributes that force download
    link.href = downloadUrl
    link.download = finalFilename
    link.style.display = 'none'

    // Additional attributes for better compatibility
    link.setAttribute('download', finalFilename)
    link.setAttribute('target', '_self')

    // Created download link

    // Add to DOM, click, and remove (required for some browsers)
    document.body.appendChild(link)

    // Try multiple click methods for maximum compatibility
    try {
      link.click()
    } catch (clickError) {
      // Standard click failed, trying dispatch event
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: false,
      })
      link.dispatchEvent(clickEvent)
    }

    // Clean up after a short delay
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link)
      }
      window.URL.revokeObjectURL(downloadUrl)
    }, 100)

    // Download initiated successfully
    return true
  } catch (error) {
    // Download failed

    // Enhanced fallback - try different approaches
    try {
      // Trying fallback download method

      // Method 1: Try with a different approach
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'ai-generated-content'
      link.style.display = 'none'
      link.setAttribute('download', filename || 'ai-generated-content')

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Fallback download method 1 attempted
      return true
    } catch (fallbackError1) {
      // Fallback method 1 failed

      try {
        // Method 2: Open in new window with download intent
        // Trying fallback method 2
        const newWindow = window.open(url, '_blank')
        if (newWindow) {
          newWindow.focus()
        }
        // Opened in new window as final fallback
        return false
      } catch (fallbackError2) {
        // All download methods failed
        return false
      }
    }
  }
}

export const ViewerControls = ({
  isDark = false,
  isFullscreen = false,
  onReset,
  onZoomIn,
  onZoomOut,
  onToggleGrid,
  onEnlarge,
  onExitFullscreen,
  onDownload,
  // Control visibility options
  showReset = true,
  showZoom = true,
  showGrid = false,
  showDownload = false,
  showFullscreen = true, // Always show fullscreen button by default
  // State props for conditional icons
  className = '',
}) => {
  const baseControlsClass = `flex flex-col gap-1 p-2 rounded-lg backdrop-blur-sm ${
    isDark ? 'bg-black/60 border border-zinc-700' : 'bg-white/60 border border-zinc-200'
  }`

  const buttonClass = 'h-10 w-10 lg:h-8 lg:w-8' // Larger touch targets on mobile

  return (
    <TooltipProvider>
      <div
        className={`absolute top-2 right-2 bg-white rounded-lg lg:top-4 lg:right-4 flex flex-col gap-1 lg:gap-2 ${
          isFullscreen ? 'z-50' : 'z-10'
        } ${className}`}
      >
        <div className={baseControlsClass}>
          {/* Close button for fullscreen */}
          {isFullscreen && onExitFullscreen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onExitFullscreen}>
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Exit Fullscreen (ESC)</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Reset button - show in both fullscreen and normal mode */}
          {showReset && onReset && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onReset}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Reset View</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Zoom In button - show in both fullscreen and normal mode */}
          {showZoom && onZoomIn && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Zoom In</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Zoom Out button - show in both fullscreen and normal mode */}
          {showZoom && onZoomOut && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Zoom Out</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Grid toggle button - show in both fullscreen and normal mode */}
          {showGrid && onToggleGrid && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onToggleGrid}>
                  <Grid3x3 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Toggle Grid</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Download button - show in both fullscreen and normal mode */}
          {showDownload && onDownload && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onDownload}>
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Download</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Fullscreen/Exit Fullscreen button - always at the bottom of controls */}
          {showFullscreen && !isFullscreen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={buttonClass} onClick={onEnlarge}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Fullscreen</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}

// Export the download function and filename generator for use in other components
export { downloadFileFromUrl, generateMeaningfulFileName }
